import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import {
  Zap,
  Search,
  Play,
  Pause,
  Square,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  Database,
  Mail,
  Upload,
  Download,
} from 'lucide-react';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { formatDistanceToNow } from 'date-fns';
import { logError, logDebug } from '@/utils/errorHandler';

interface BackgroundJob {
  id: string;
  name: string;
  type: 'import' | 'export' | 'email' | 'backup' | 'cleanup' | 'sync';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  startedAt: string | null;
  completedAt: string | null;
  duration: number | null;
  organizationId: string;
  organizationName: string;
  userId: string;
  userName: string;
  details: Record<string, any>;
  errorMessage?: string;
  priority: 'low' | 'normal' | 'high';
}

interface JobStats {
  total: number;
  running: number;
  completed: number;
  failed: number;
  queued: number;
}

export function BackgroundJobs() {
  const { hasPermission } = useAdminPermissions();
  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<BackgroundJob[]>([]);
  const [jobStats, setJobStats] = useState<JobStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    search: '',
    type: 'all',
    status: 'all',
    organization: 'all',
    priority: 'all',
  });

  // Fetch jobs from API
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        // TODO: Replace with actual API call
        // const response = await fetch('/api/admin/jobs')
        // const data = await response.json()

        // Mock data
        const mockJobs: BackgroundJob[] = [
          {
            id: '1',
            name: 'Product Import - Electronics Catalog',
            type: 'import',
            status: 'running',
            progress: 65,
            startedAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
            completedAt: null,
            duration: null,
            organizationId: 'org1',
            organizationName: 'Acme Corp',
            userId: 'user1',
            userName: 'John Manager',
            details: {
              totalProducts: 1500,
              processedProducts: 975,
              filePath: '/uploads/electronics_catalog.xlsx',
            },
            priority: 'high',
          },
          {
            id: '2',
            name: 'Daily Backup',
            type: 'backup',
            status: 'completed',
            progress: 100,
            startedAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
            completedAt: new Date(Date.now() - 1000 * 60 * 60 * 5.5).toISOString(),
            duration: 1800,
            organizationId: 'system',
            organizationName: 'System',
            userId: 'system',
            userName: 'System',
            details: {
              backupSize: '2.3 GB',
              destination: 's3://kernlogic-backups/daily/',
              tablesBackedUp: 23,
            },
            priority: 'normal',
          },
          {
            id: '3',
            name: 'Email Campaign - Product Updates',
            type: 'email',
            status: 'failed',
            progress: 25,
            startedAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
            completedAt: new Date(Date.now() - 1000 * 60 * 60 * 1.8).toISOString(),
            duration: 720,
            organizationId: 'org2',
            organizationName: 'Global Retail',
            userId: 'user2',
            userName: 'Jane Marketing',
            details: {
              totalRecipients: 1200,
              sentEmails: 300,
              template: 'product_updates_v2',
            },
            errorMessage: 'SMTP server connection timeout',
            priority: 'normal',
          },
          {
            id: '4',
            name: 'Product Export - Full Catalog',
            type: 'export',
            status: 'pending',
            progress: 0,
            startedAt: null,
            completedAt: null,
            duration: null,
            organizationId: 'org1',
            organizationName: 'Acme Corp',
            userId: 'user3',
            userName: 'Bob Analyst',
            details: {
              format: 'CSV',
              filters: { category: 'Electronics', status: 'active' },
              estimatedRecords: 850,
            },
            priority: 'low',
          },
        ];

        const stats: JobStats = {
          total: mockJobs.length,
          running: mockJobs.filter(j => j.status === 'running').length,
          completed: mockJobs.filter(j => j.status === 'completed').length,
          failed: mockJobs.filter(j => j.status === 'failed').length,
          queued: mockJobs.filter(j => j.status === 'pending').length,
        };

        setJobs(mockJobs);
        setFilteredJobs(mockJobs);
        setJobStats(stats);
      } catch (error) {
        logError(
          'Failed to fetch background jobs',
          {
            component: 'BackgroundJobs',
            action: 'fetchJobs',
            metadata: {
              error,
            },
          },
          {
            showToast: true,
            toastMessage: 'Failed to load background jobs',
            userFacing: true,
          }
        );
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();

    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchJobs, 10000);
    return () => clearInterval(interval);
  }, []);

  // Filter jobs
  useEffect(() => {
    let filtered = jobs;

    if (filter.search) {
      filtered = filtered.filter(
        job =>
          job.name.toLowerCase().includes(filter.search.toLowerCase()) ||
          job.organizationName.toLowerCase().includes(filter.search.toLowerCase()) ||
          job.userName.toLowerCase().includes(filter.search.toLowerCase())
      );
    }

    if (filter.type !== 'all') {
      filtered = filtered.filter(job => job.type === filter.type);
    }

    if (filter.status !== 'all') {
      filtered = filtered.filter(job => job.status === filter.status);
    }

    if (filter.organization !== 'all') {
      filtered = filtered.filter(job => job.organizationId === filter.organization);
    }

    if (filter.priority !== 'all') {
      filtered = filtered.filter(job => job.priority === filter.priority);
    }

    setFilteredJobs(filtered);
  }, [jobs, filter]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-gray-100 text-gray-800">Pending</Badge>;
      case 'running':
        return <Badge className="bg-blue-100 text-blue-800">Running</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
      case 'cancelled':
        return <Badge className="bg-yellow-100 text-yellow-800">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="size-4 text-gray-600" />;
      case 'running':
        return <Play className="size-4 text-blue-600" />;
      case 'completed':
        return <CheckCircle className="size-4 text-green-600" />;
      case 'failed':
        return <XCircle className="size-4 text-red-600" />;
      case 'cancelled':
        return <Square className="size-4 text-yellow-600" />;
      default:
        return <Activity className="size-4 text-gray-600" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'import':
        return <Upload className="size-4" />;
      case 'export':
        return <Download className="size-4" />;
      case 'email':
        return <Mail className="size-4" />;
      case 'backup':
        return <Database className="size-4" />;
      case 'cleanup':
        return <RefreshCw className="size-4" />;
      case 'sync':
        return <Activity className="size-4" />;
      default:
        return <Zap className="size-4" />;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'low':
        return (
          <Badge variant="outline" className="text-gray-600">
            Low
          </Badge>
        );
      case 'normal':
        return (
          <Badge variant="outline" className="text-blue-600">
            Normal
          </Badge>
        );
      case 'high':
        return (
          <Badge variant="outline" className="text-red-600">
            High
          </Badge>
        );
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const handleJobAction = async (jobId: string, action: 'start' | 'pause' | 'cancel') => {
    try {
      // TODO: API call to control job
      logDebug(`Performing ${action} on job ${jobId}`, {
        component: 'BackgroundJobs',
        action: 'jobAction',
        metadata: {
          jobId,
          action,
        },
      });
    } catch (error) {
      logError(
        `Failed to ${action} job`,
        {
          component: 'BackgroundJobs',
          action: 'jobAction',
          metadata: {
            jobId,
            action,
            error,
          },
        },
        {
          showToast: true,
          toastMessage: `Failed to ${action} job`,
          userFacing: true,
        }
      );
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  if (!hasPermission('admin.system.view')) {
    return (
      <AdminLayout title="Access Denied">
        <div className="py-8 text-center">
          <p>You don't have permission to view background jobs.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Background Jobs" description="Monitor and manage system background tasks">
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search jobs..."
                value={filter.search}
                onChange={e => setFilter({ ...filter, search: e.target.value })}
                className="w-64 pl-10"
              />
            </div>

            <Select
              value={filter.type}
              onValueChange={value => setFilter({ ...filter, type: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="import">Import</SelectItem>
                <SelectItem value="export">Export</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="backup">Backup</SelectItem>
                <SelectItem value="cleanup">Cleanup</SelectItem>
                <SelectItem value="sync">Sync</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.status}
              onValueChange={value => setFilter({ ...filter, status: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.priority}
              onValueChange={value => setFilter({ ...filter, priority: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button variant="outline">
            <RefreshCw className="mr-2 size-4" />
            Refresh
          </Button>
        </div>

        {/* Job Statistics */}
        {jobStats && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
                <Zap className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{jobStats.total}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Running</CardTitle>
                <Play className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{jobStats.running}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Queued</CardTitle>
                <Clock className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{jobStats.queued}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Completed</CardTitle>
                <CheckCircle className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{jobStats.completed}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Failed</CardTitle>
                <XCircle className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{jobStats.failed}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Jobs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Background Jobs ({filteredJobs.length})</CardTitle>
            <CardDescription>
              Monitor and manage system background tasks and processes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="py-8 text-center">
                      Loading jobs...
                    </TableCell>
                  </TableRow>
                ) : filteredJobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="py-8 text-center">
                      No jobs found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredJobs.map(job => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(job.status)}
                          {getTypeIcon(job.type)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{job.name}</div>
                          {job.errorMessage && (
                            <div className="mt-1 text-xs text-red-600">
                              Error: {job.errorMessage}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(job.status)}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Progress value={job.progress} className="h-2 w-20" />
                          <span className="text-xs text-muted-foreground">{job.progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{job.organizationName}</TableCell>
                      <TableCell>{job.userName}</TableCell>
                      <TableCell>
                        {job.startedAt ? (
                          <span className="text-sm">
                            {formatDistanceToNow(new Date(job.startedAt), {
                              addSuffix: true,
                            })}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">Not started</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{formatDuration(job.duration)}</span>
                      </TableCell>
                      <TableCell>{getPriorityBadge(job.priority)}</TableCell>
                      <TableCell>
                        {hasPermission('admin.system.manage') && job.status === 'running' && (
                          <div className="flex space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleJobAction(job.id, 'pause')}
                            >
                              <Pause className="size-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleJobAction(job.id, 'cancel')}
                            >
                              <Square className="size-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
